// AppShell — production authenticated layout (Wave 5a Agent 5a.2).
//
// Replaces the W4.1 stub at `app/_authed/__layout.tsx`. Composes:
//   - DS `AppShell` (sidebar + content stack + banner row)
//   - feature `Sidebar` (brand + nav + user-card dropdown)
//   - feature `TrialBanner` (US-191) — wired to subscription state
//   - DS `FtuWelcomeBanner` placement (US-057) — gated on FTU active state
//   - min-viewport guard (US-059) — desktop-only splash below 1024px
//   - SkipLink (US-255 prefactor)
//
// The shell mounts `useVisitedPages()` (US-056) so route changes auto-record
// page ids for FTU completion.

import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import {
  invalidateConversations,
  useConversations,
  useLinkedinAccounts,
  useOnboardingState,
  useSeedDemo,
  useUserHubChannel,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { AppShell as DsAppShell, toast } from '@barel/ui';

import { useAuthGuard } from '@/lib/auth';
import { HardBlockRedirect } from '@/features/billing/HardBlockRedirect';
import { FtuChecklistContainer } from '@/features/ftu/Checklist';
import { FtuWelcomeBannerContainer } from '@/features/ftu/WelcomeBanner';
import { NotificationListener } from '@/lib/notifications/NotificationListener';
import { OnboardingShell } from '@/features/onboarding/OnboardingShell';

import { OnboardingRedirect } from './OnboardingRedirect';
import { Sidebar } from './Sidebar';
import { APP_MAIN_LANDMARK_ID } from './SkipLink';
import { TrialBanner } from './TrialBanner';
import { useVisitedPages } from './visitedPages';

const MIN_VIEWPORT_WIDTH = 1024;

interface FtuState {
  /** Whether onboarding is complete and the user is in their first session. */
  active: boolean;
  /** User-facing goal — required by `<FtuWelcomeBanner>`. */
  goal: 'inbox' | 'campaigns' | 'both';
  /** Per-session dismissal state. */
  dismissed: boolean;
}

const FTU_DISMISSED_KEY = 'barel:ftu:bannerDismissed';

/**
 * `useFtuState()` — derives the shell-banner state from the live
 * `/me/onboarding` query. The banner shows once the user has completed
 * onboarding (`completedAt != null`) and hasn't dismissed it yet. Dismissal
 * persists in `localStorage` so a hard refresh respects the user's choice.
 *
 * Demo-data restoration / explicit FTU exit will land with the demo-seed
 * service; flipping `active` back off then is a one-line change here.
 */
function useFtuState(): [FtuState, (next: Partial<FtuState>) => void] {
  const onboarding = useOnboardingState();
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && window.localStorage.getItem(FTU_DISMISSED_KEY) === '1',
  );

  const goal: FtuState['goal'] = onboarding.data?.goal ?? 'both';
  const active = !!onboarding.data?.completedAt;

  const update = (next: Partial<FtuState>): void => {
    if (typeof next.dismissed === 'boolean') {
      setDismissed(next.dismissed);
      if (next.dismissed && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(FTU_DISMISSED_KEY, '1');
        } catch {
          /* noop */
        }
      }
    }
  };

  return [{ active, goal, dismissed }, update];
}

/**
 * Watch the viewport width and toggle the desktop-only splash below the
 * `MIN_VIEWPORT_WIDTH` threshold (US-059).
 */
function useIsDesktopViewport(): boolean {
  const [ok, setOk] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= MIN_VIEWPORT_WIDTH;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (): void => {
      setOk(window.innerWidth >= MIN_VIEWPORT_WIDTH);
    };
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);
  return ok;
}

/**
 * Min-viewport splash (US-059). Rendered when the window width is below
 * 1024px. Pure presentational; tokens drive every value.
 */
function MinViewportSplash(): ReactNode {
  const t = useT('shell');
  return (
    <div
      data-region="min-viewport-splash"
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-6)',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        zIndex: 'var(--z-modal)' as unknown as number,
      }}
    >
      <h1 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 700 }}>
        {t('minViewport.title')}
      </h1>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
        {t('minViewport.body')}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-2xs)',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {t('minViewport.tagline')}
      </p>
    </div>
  );
}

export interface AppShellProps {
  /**
   * Inbox unread state (US-041). Omit to derive from live conversations
   * (any `unread: true` row in the most recent page); pass explicitly to
   * force it on/off (tests, Storybook).
   */
  hasUnread?: boolean;
  /**
   * Accounts alert (US-042). Omit to derive from live LinkedIn accounts
   * (any account in `error` status — i.e. needs reconnecting); pass
   * explicitly to force it on/off (tests, Storybook).
   */
  accountAlert?: boolean;
}

/**
 * `<AppShell>` — production authenticated layout.
 */
// Routes that surface FTU affordances (welcome banner + floating checklist).
// Per spec the banner + checklist only render on inbox + campaigns.
const FTU_PATH_PREFIXES = ['/inbox', '/campaigns'] as const;

function isFtuRoute(pathname: string): boolean {
  // The expanded fixed checklist covers the campaign wizard's bottom-right
  // actions. Keep the first-session guidance on campaign landing/detail
  // surfaces, but never place it over the creation form itself.
  if (pathname === '/campaigns/new' || pathname.startsWith('/campaigns/new/')) return false;
  return FTU_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function AppShell({
  hasUnread: hasUnreadOverride,
  accountAlert: accountAlertOverride,
}: AppShellProps) {
  const t = useT('shell');
  const tFtu = useT('ftu');
  const auth = useAuthGuard();
  const navigate = useNavigate();
  const desktop = useIsDesktopViewport();
  const [ftu, setFtu] = useFtuState();
  const router = useRouterState();
  const seedDemo = useSeedDemo();
  const qc = useQueryClient();

  // US-041/US-042 — sidebar notification dots. No dedicated "unread count" /
  // "needs attention" summary endpoint exists, so we derive both from the
  // same list queries the Inbox + Accounts pages already use, and keep them
  // live via the realtime events those pages subscribe to.
  const conversationsQuery = useConversations({ limit: 20 });
  const linkedinAccountsQuery = useLinkedinAccounts();
  useUserHubChannel('message.in', () => invalidateConversations(qc));
  useUserHubChannel('account.status', () => {
    void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
  });
  const derivedHasUnread = (conversationsQuery.data?.pages ?? []).some((page) =>
    page.conversations.some((conv) => conv.unread),
  );
  const derivedAccountAlert = (linkedinAccountsQuery.data?.accounts ?? []).some(
    (account) => account.status === 'error',
  );
  const hasUnread = hasUnreadOverride ?? derivedHasUnread;
  const accountAlert = accountAlertOverride ?? derivedAccountAlert;

  // US-056 — record visited routes for FTU completion. Mounting the hook is
  // enough; the internal subscription auto-fires on every navigation.
  useVisitedPages();

  if (!desktop) {
    return <MinViewportSplash />;
  }

  const pathname = router.location.pathname;
  // US-049 — onboarding wizard renders chrome-free. We branch *after* the
  // viewport guard but *before* the sidebar/banner stack so the wizard owns
  // the full viewport. `OnboardingRedirect` still ensures incomplete users
  // land here automatically.
  if (pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return (
      <>
        {/* Mount the redirect so a finished user navigating back to /onboarding
            still gets booted out per US-029. */}
        <OnboardingRedirect />
        <OnboardingShell />
      </>
    );
  }

  const ftuRoute = isFtuRoute(pathname);
  const showFtu = ftu.active && !ftu.dismissed && ftuRoute;

  const handleLoadDemo = async (): Promise<void> => {
    try {
      await seedDemo.mutateAsync();
      // Flip the FTU surface off — the demo data is loaded so the user
      // shouldn't see "first session" affordances anymore.
      setFtu({ dismissed: true });
      toast.success({ title: tFtu('demo.loadedToast') });
    } catch {
      toast.error({ title: tFtu('demo.errorToast') });
    }
  };

  const banner: ReactNode = (
    <>
      <TrialBanner
        onUpgrade={() => {
          void navigate({ to: '/settings/$section', params: { section: 'billing' } });
        }}
      />
      {showFtu ? (
        <FtuWelcomeBannerContainer goal={ftu.goal} onLoadDemo={() => void handleLoadDemo()} />
      ) : null}
    </>
  );

  return (
    <>
      {/* US-029 — fresh user lands in `/onboarding` until the wizard completes. */}
      <OnboardingRedirect />
      {/* US-200 — auto-redirect to billing once trial hard-block kicks in. */}
      <HardBlockRedirect />
      {/* D22 (Q10) — surface realtime `notification.*` events as in-app
          toasts. Mounted under the authed shell so anonymous surfaces
          never subscribe and we don't leak across logout. */}
      <NotificationListener />
      {/* SkipLink is mounted globally in `__root.tsx` (US-255) so it covers
          AppShell + onboarding + auth surfaces with a single instance. The DS
          `AppShell` still renders its internal `<main id={APP_MAIN_LANDMARK_ID}>`
          which both the global SkipLink and the route-change focus reset
          target. */}
      <DsAppShell
        sidebar={<Sidebar user={auth.user} hasUnread={hasUnread} accountAlert={accountAlert} />}
        banner={banner}
        asideLabel={t('asideLabel')}
        skipLinkLabel={t('skipLink.label')}
        skipTargetId={APP_MAIN_LANDMARK_ID}
        main={
          <div data-region="authed-content" style={{ padding: 'var(--space-6)' }}>
            <Outlet />
          </div>
        }
      />
      {/* US-033 / US-035 — floating checklist, gated to inbox + campaigns.
          Rendered as a fixed-position widget because the DS shell doesn't
          expose an `aside` slot for the right dock. */}
      {showFtu ? (
        <div
          data-region="ftu-checklist-dock"
          style={{
            position: 'fixed',
            right: 'var(--space-6)',
            bottom: 'var(--space-6)',
            zIndex: 50,
            maxWidth: 320,
          }}
        >
          <FtuChecklistContainer />
        </div>
      ) : null}
    </>
  );
}
